import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut, ArrowLeft, Upload, Images } from "lucide-react";
import { toast } from "sonner";
import { authService, adminCatalogService, seasonPicksService, catalogService, formatApiError, resolveImageUrl } from "../services/catalogService";
import CatalogPanel from "../components/admin/CatalogPanel";
import BlogPanel from "../components/admin/BlogPanel";
import PartnersPanel from "../components/admin/PartnersPanel";
import CertificationsPanel from "../components/admin/CertificationsPanel";
import LeadsPanel from "../components/admin/LeadsPanel";
import PopupPanel from "../components/admin/PopupPanel";
import AnalyticsPanel from "../components/admin/AnalyticsPanel";
import DashboardPanel from "../components/admin/DashboardPanel";
import MilestonesPanel from "../components/admin/MilestonesPanel";

function LoginForm({ onLogged }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await authService.login(email, password);
      onLogged(data);
    } catch (err) {
      toast.error("Could not sign in", { description: formatApiError(err) });
    } finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="max-w-md mx-auto bg-white border border-black/10 p-10" data-testid="admin-login-form">
      <h1 className="font-serif text-3xl text-[#252525]">Admin access</h1>
      <p className="text-[#817B74] text-sm mt-2 mb-8">Authorized personnel only.</p>
      <label className="block text-[11px] uppercase tracking-[0.14em] text-[#817B74] mb-1">Email</label>
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
        data-testid="admin-email"
        className="w-full bg-transparent border-b border-black/15 py-2 mb-5 focus:outline-none focus:border-[#D92A47]" />
      <label className="block text-[11px] uppercase tracking-[0.14em] text-[#817B74] mb-1">Password</label>
      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
        data-testid="admin-password"
        className="w-full bg-transparent border-b border-black/15 py-2 mb-8 focus:outline-none focus:border-[#D92A47]" />
      <button type="submit" disabled={loading} data-testid="admin-login-submit"
        className="w-full h-12 bg-[#D92A47] text-[#F6F2EC] text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a] inline-flex items-center justify-center gap-2 disabled:opacity-70">
        {loading ? <><Loader2 size={14} className="animate-spin" /> Signing in…</> : "Sign in"}
      </button>
    </form>
  );
}

function OrdersList({ admin, onLogout }) {
  const [orders, setOrders] = useState(null);
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    authService.listOrders().then(setOrders).catch((e) => setErr(formatApiError(e)));
  }, []);
  const fmt = (d) => d ? new Date(d).toLocaleString() : "—";
  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-orders-panel">
      <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">Admin</span>
          <h1 className="font-serif text-4xl md:text-5xl text-[#252525] mt-2">Order requests</h1>
          <p className="text-[#817B74] text-sm mt-1">Signed in as {admin.email}</p>
        </div>
        <button onClick={onLogout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.14em] hover:border-[#D92A47] hover:text-[#D92A47]">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {err && <p role="alert" className="text-[#D92A47] mb-4">{err}</p>}
      {orders === null ? (
        <div className="text-[#817B74] py-20 text-center"><Loader2 className="animate-spin inline mr-2" size={16}/>Loading…</div>
      ) : orders.length === 0 ? (
        <div className="border border-black/10 bg-white p-10 text-center text-[#817B74]" data-testid="admin-orders-empty">No order requests yet.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <ul className="lg:col-span-2 bg-white border border-black/10 divide-y divide-black/10" data-testid="admin-orders-list">
            {orders.map((o) => (
              <li key={o.id}>
                <button type="button" onClick={() => setSelected(o)} data-testid={`admin-order-${o.reference}`}
                  className={`w-full text-left p-4 hover:bg-[#F6F2EC] ${selected?.id === o.id ? "bg-[#F6F2EC]" : ""}`}>
                  <div className="font-mono text-xs text-[#D92A47]">{o.reference}</div>
                  <div className="font-serif text-lg mt-1">{o.contact_name}</div>
                  <div className="text-xs text-[#817B74]">{o.email} · {fmt(o.received_at)}</div>
                  <div className="text-xs mt-1">{o.lines?.length || 0} line(s)</div>
                </button>
              </li>
            ))}
          </ul>
          <section className="lg:col-span-3 bg-white border border-black/10 p-6" data-testid="admin-order-detail">
            {!selected ? (
              <p className="text-[#817B74]">Select a request to view its details.</p>
            ) : (
              <>
                <div className="font-mono text-xs text-[#D92A47]">{selected.reference}</div>
                <h2 className="font-serif text-2xl mt-1">{selected.contact_name}</h2>
                <div className="text-sm text-[#817B74]">{selected.email}</div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mt-5">
                  <dt className="text-[#817B74]">Company</dt><dd>{selected.company || "—"}</dd>
                  <dt className="text-[#817B74]">Country</dt><dd>{selected.country || "—"}</dd>
                  <dt className="text-[#817B74]">Business type</dt><dd>{selected.business_type || "—"}</dd>
                  <dt className="text-[#817B74]">Language</dt><dd>{selected.language || "—"}</dd>
                  <dt className="text-[#817B74]">Source page</dt><dd className="truncate">{selected.source_page || "—"}</dd>
                  <dt className="text-[#817B74]">Received</dt><dd>{fmt(selected.received_at)}</dd>
                </dl>
                {selected.message && <p className="mt-5 bg-[#F6F2EC] p-4 text-sm italic text-[#5f5a54]">{selected.message}</p>}
                <h3 className="font-serif text-lg mt-6">Lines</h3>
                <ul className="divide-y divide-black/10 border border-black/10 mt-2">
                  {(selected.lines || []).map((l, i) => (
                    <li key={i} className="p-3 flex justify-between gap-4 text-sm">
                      <span>{l.variety_name}</span>
                      <span className="text-[#817B74]">{l.quantity} · <code>{l.unit}</code></span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function ImageSlot({ variety, slot, onReplaced }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const src = resolveImageUrl(variety.images?.[slot - 1] || variety.image);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const updated = await adminCatalogService.replaceVarietyImage(variety.id, slot, file);
      onReplaced(updated);
      toast.success(`Photo ${slot} replaced`, { description: variety.name });
    } catch (err) {
      toast.error("Upload failed", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-black/10 bg-[#F6F2EC]" data-testid={`slot-${variety.id}-${slot}`}>
      <div className="relative aspect-square">
        {src && <img src={src} alt={`${variety.name} view ${slot}`} className="absolute inset-0 h-full w-full object-cover" />}
        <span className="absolute top-2 left-2 bg-white/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#252525]">
          Photo {slot}
        </span>
      </div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`replace-${variety.id}-${slot}`}
        className="w-full inline-flex items-center justify-center gap-2 h-9 bg-white text-[10px] font-semibold uppercase tracking-[0.14em] text-[#252525] hover:bg-[#D92A47] hover:text-[#F6F2EC] transition-colors disabled:opacity-60"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
        {busy ? "Uploading…" : "Replace"}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={onFile}
        data-testid={`file-${variety.id}-${slot}`}
      />
    </div>
  );
}

function VarietyPhotosPanel({ admin, onLogout }) {
  const [varieties, setVarieties] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    adminCatalogService.listVarieties().then(setVarieties).catch((e) => setErr(formatApiError(e)));
  }, []);

  const onReplaced = (updated) => {
    setVarieties((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-photos-panel">
      <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">Admin</span>
          <h1 className="font-serif text-4xl md:text-5xl text-[#252525] mt-2">Variety photos</h1>
          <p className="text-[#817B74] text-sm mt-1">
            Four image views per variety. Until the final photographs arrive, every slot shows the
            same placeholder — replace each one independently. Signed in as {admin.email}
          </p>
        </div>
        <button onClick={onLogout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.14em] hover:border-[#D92A47] hover:text-[#D92A47]">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {err && <p role="alert" className="text-[#D92A47] mb-4">{err}</p>}
      {varieties === null ? (
        <div className="text-[#817B74] py-20 text-center"><Loader2 className="animate-spin inline mr-2" size={16}/>Loading…</div>
      ) : varieties.length === 0 ? (
        <div className="border border-black/10 bg-white p-10 text-center text-[#817B74]" data-testid="admin-photos-empty">
          <Images size={24} strokeWidth={1.4} className="mx-auto mb-3 text-[#D92A47]" />
          No varieties in the catalog yet.
        </div>
      ) : (
        <div className="space-y-8">
          {varieties.map((v) => (
            <section key={v.id} className="bg-white border border-black/10 p-5" data-testid={`photos-variety-${v.id}`}>
              <div className="flex items-baseline justify-between gap-4 flex-wrap mb-4">
                <h2 className="font-serif text-2xl text-[#252525]">{v.name}</h2>
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">{v.category_label}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[1, 2, 3, 4].map((slot) => (
                  <ImageSlot key={`${v.id}-${slot}-${v.images?.[slot - 1] || ""}`} variety={v} slot={slot} onReplaced={onReplaced} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function SeasonPicksPanel({ admin, onLogout }) {
  const [facets, setFacets] = useState(null);
  const [categoryId, setCategoryId] = useState("");
  const [colorFamily, setColorFamily] = useState("");
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([catalogService.getFacets(), seasonPicksService.getAdmin()])
      .then(([f, cfg]) => {
        setFacets(f);
        setCategoryId(cfg.category_id || "");
        setColorFamily(cfg.color_family || "");
        setMatches(cfg.matches || []);
        setLoaded(true);
      })
      .catch((e) => toast.error("Could not load Season Picks", { description: formatApiError(e) }));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const res = await seasonPicksService.save({
        category_id: categoryId || null,
        color_family: colorFamily || null,
      });
      setMatches(res.matches || []);
      toast.success("Season Picks updated", { description: `${res.match_count} matching varieties` });
    } catch (err) {
      toast.error("Could not save", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const selectCls = "h-11 bg-white border border-black/15 px-3 text-sm focus:outline-none focus:border-[#D92A47]";

  return (
    <div className="max-w-3xl mx-auto" data-testid="admin-season-panel">
      <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">Admin</span>
          <h1 className="font-serif text-4xl md:text-5xl text-[#252525] mt-2">Season Picks</h1>
          <p className="text-[#817B74] text-sm mt-1">
            Choose a collection and a color — the home block shows the matching varieties. Signed in as {admin.email}
          </p>
        </div>
        <button onClick={onLogout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.14em] hover:border-[#D92A47] hover:text-[#D92A47]">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {!loaded ? (
        <div className="text-[#817B74] py-20 text-center"><Loader2 className="animate-spin inline mr-2" size={16}/>Loading…</div>
      ) : (
        <div className="bg-white border border-black/10 p-6 md:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.14em] text-[#817B74] mb-1">Collection</span>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} data-testid="season-filter-category" className={`w-full ${selectCls}`}>
                <option value="">Any collection</option>
                {(facets?.categories || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.14em] text-[#817B74] mb-1">Color</span>
              <select value={colorFamily} onChange={(e) => setColorFamily(e.target.value)} data-testid="season-filter-color" className={`w-full ${selectCls}`}>
                <option value="">Any color</option>
                {(facets?.colors || []).map((c) => (
                  <option key={c.family} value={c.family}>{c.label}</option>
                ))}
              </select>
            </label>
          </div>

          <button
            onClick={save}
            disabled={busy || (!categoryId && !colorFamily)}
            data-testid="season-save"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 bg-[#D92A47] text-[#F6F2EC] text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a] disabled:opacity-50"
          >
            {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Save Season Picks"}
          </button>

          <div className="mt-6" data-testid="season-matches">
            <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">
              Currently showing ({matches.length})
            </span>
            {matches.length === 0 ? (
              <p className="text-sm text-[#817B74] mt-2">No varieties match — the home block stays hidden.</p>
            ) : (
              <ul className="flex flex-wrap gap-2 mt-2">
                {matches.map((m) => (
                  <li key={m.id} className="font-serif italic text-[15px] border border-black/10 px-3 py-1.5" data-testid={`season-match-${m.id}`}>
                    {m.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DescriptionRow({ variety, onSaved }) {
  const [text, setText] = useState(variety.description_en || "");
  const [busy, setBusy] = useState(false);
  const dirty = text.trim() !== (variety.description_en || "").trim();

  const save = async () => {
    setBusy(true);
    try {
      const updated = await adminCatalogService.updateVarietyDescription(variety.id, text);
      onSaved(updated);
      toast.success("Description saved", { description: variety.name });
    } catch (err) {
      toast.error("Could not save", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-black/10 p-5" data-testid={`desc-row-${variety.id}`}>
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-3">
        <h2 className="font-serif text-2xl text-[#252525]">{variety.name}</h2>
        <span className="text-[11px] uppercase tracking-[0.14em] text-[#817B74]">{variety.category_label}</span>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder="Description shown wherever this variety is selected (Season Picks, detail page)…"
        data-testid={`desc-input-${variety.id}`}
        className="w-full bg-[#F6F2EC] border border-black/10 p-3 text-sm leading-relaxed focus:outline-none focus:border-[#D92A47] resize-y"
      />
      <div className="flex items-center justify-between mt-3">
        <span className="text-[11px] text-[#817B74]">{text.length}/2000</span>
        <button
          onClick={save}
          disabled={busy || !dirty}
          data-testid={`desc-save-${variety.id}`}
          className="inline-flex items-center gap-2 h-10 px-5 bg-[#D92A47] text-[#F6F2EC] text-[11px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a] disabled:opacity-50"
        >
          {busy ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : "Save description"}
        </button>
      </div>
    </section>
  );
}

function DescriptionsPanel({ admin, onLogout }) {
  const [varieties, setVarieties] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    adminCatalogService.listVarieties().then(setVarieties).catch((e) => setErr(formatApiError(e)));
  }, []);

  const onSaved = (updated) => {
    setVarieties((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="admin-descriptions-panel">
      <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">Admin</span>
          <h1 className="font-serif text-4xl md:text-5xl text-[#252525] mt-2">Variety descriptions</h1>
          <p className="text-[#817B74] text-sm mt-1">
            This text appears automatically wherever the variety is selected — Season Picks, catalog and
            detail page. Signed in as {admin.email}
          </p>
        </div>
        <button onClick={onLogout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.14em] hover:border-[#D92A47] hover:text-[#D92A47]">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {err && <p role="alert" className="text-[#D92A47] mb-4">{err}</p>}
      {varieties === null ? (
        <div className="text-[#817B74] py-20 text-center"><Loader2 className="animate-spin inline mr-2" size={16}/>Loading…</div>
      ) : (
        <div className="space-y-6">
          {varieties.map((v) => (
            <DescriptionRow key={`${v.id}-${v.description_en?.length ?? 0}`} variety={v} onSaved={onSaved} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [admin, setAdmin] = useState(undefined); // undefined=loading, null=anon, obj=auth
  const [tab, setTabState] = useState(() => localStorage.getItem("tessa_admin_tab") || "dashboard");
  const setTab = (next) => {
    setTabState(next);
    localStorage.setItem("tessa_admin_tab", next);
  };
  useEffect(() => {
    authService.me().then((me) => setAdmin(me || null));
  }, []);
  const onLogged = (data) => setAdmin({ email: data.email, id: data.id, role: data.role });
  const onLogout = async () => { await authService.logout(); setAdmin(null); };
  return (
    <main className="min-h-screen bg-[#F6F2EC] text-[#252525] pt-28 pb-24" data-testid="admin-page">
      <div className="container-tessa">
        <Link to="/" className="flex w-fit items-center gap-2 text-[12px] uppercase tracking-[0.16em] text-[#817B74] hover:text-[#D92A47] mb-8">
          <ArrowLeft size={14} /> Back to home
        </Link>
        {admin === undefined ? (
          <div className="text-[#817B74] py-20 text-center"><Loader2 className="animate-spin inline mr-2" size={16}/>Checking session…</div>
        ) : admin ? (
          <>
            <div className="max-w-6xl mx-auto flex flex-wrap gap-x-6 gap-y-1 mb-10 border-b border-black/10" data-testid="admin-tabs" role="tablist">
              {[["dashboard", "Dashboard"], ["orders", "Order requests"], ["leads", "Leads"], ["analytics", "Analytics"], ["popup", "Popup"], ["catalog", "Catalog"], ["photos", "Variety photos"], ["descriptions", "Descriptions"], ["season", "Season Picks"], ["about", "About"], ["blog", "Blog"], ["partners", "Partners"], ["certifications", "Certifications"]].map(([key, label]) => (
                <button
                  key={key}
                  role="tab"
                  aria-selected={tab === key}
                  onClick={() => setTab(key)}
                  data-testid={`admin-tab-${key}`}
                  className={`text-[11px] uppercase tracking-[0.16em] pb-3 border-b-2 transition-colors ${
                    tab === key ? "border-[#D92A47] text-[#252525]" : "border-transparent text-[#817B74] hover:text-[#252525]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {tab === "dashboard" ? (
              <DashboardPanel admin={admin} onLogout={onLogout} onNavigate={setTab} />
            ) : tab === "orders" ? (
              <OrdersList admin={admin} onLogout={onLogout} />
            ) : tab === "leads" ? (
              <LeadsPanel admin={admin} onLogout={onLogout} />
            ) : tab === "analytics" ? (
              <AnalyticsPanel admin={admin} onLogout={onLogout} />
            ) : tab === "popup" ? (
              <PopupPanel admin={admin} onLogout={onLogout} />
            ) : tab === "catalog" ? (
              <CatalogPanel admin={admin} onLogout={onLogout} />
            ) : tab === "photos" ? (
              <VarietyPhotosPanel admin={admin} onLogout={onLogout} />
            ) : tab === "descriptions" ? (
              <DescriptionsPanel admin={admin} onLogout={onLogout} />
            ) : tab === "season" ? (
              <SeasonPicksPanel admin={admin} onLogout={onLogout} />
            ) : tab === "about" ? (
              <MilestonesPanel />
            ) : tab === "blog" ? (
              <BlogPanel admin={admin} onLogout={onLogout} />
            ) : tab === "partners" ? (
              <PartnersPanel admin={admin} onLogout={onLogout} />
            ) : (
              <CertificationsPanel admin={admin} onLogout={onLogout} />
            )}
          </>
        ) : (
          <LoginForm onLogged={onLogged} />
        )}
      </div>
    </main>
  );
}
